import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader, CheckCircle, XCircle } from "lucide-react";
import BaseApi from "../services/base-api";
import { useTranslation } from "react-i18next";

const PaymentCallback = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    handlePaymentCallback();
  }, []);

  const handlePaymentCallback = async () => {
    try {
      // Extract payment ID from URL parameters
      const paymentId =
        searchParams.get("paymentId") || searchParams.get("PaymentId");
      const orderId =
        searchParams.get("orderId") ||
        searchParams.get("OrderId") ||
        searchParams.get("oid") ||
        extractOrderIdFromUrl();

      // console.log(
      //   "Payment callback - paymentId:",
      //   paymentId,
      //   "orderId:",
      //   orderId,
      // );

      if (!orderId) {
        throw new Error("Order ID not found");
      }

      // Verify payment status with backend
      const response = await BaseApi.get(`/payments/status/${orderId}`);

      const paid =
        response?.success &&
        (response?.data?.status === "paid" ||
          response?.data?.paymentStatus === "paid");

      if (paid) {
        setStatus("success");
        setMessage(t("Payment completed successfully!"));

        // Popup flow support (legacy)
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "PAYMENT_CALLBACK",
              orderId: orderId,
              status: "success",
            },
            window.location.origin,
          );
          setTimeout(() => {
            window.close();
          }, 1200);
          return;
        }

        // Same-tab flow: return to checkout success screen
        setTimeout(() => {
          navigate("/checkout", {
            replace: true,
            state: {
              paymentSuccess: true,
              orderId,
            },
          });
        }, 800);
      } else {
        throw new Error(
          response?.data?.message || "Payment verification failed",
        );
      }
    } catch (error: any) {
      console.error("Payment callback error:", error);
      setStatus("error");
      setMessage(error.message || t("Payment verification failed"));

      // Popup flow support (legacy)
      const orderId =
        searchParams.get("orderId") ||
        searchParams.get("OrderId") ||
        searchParams.get("oid") ||
        extractOrderIdFromUrl();
      if (window.opener && orderId) {
        window.opener.postMessage(
          {
            type: "PAYMENT_CALLBACK",
            orderId: orderId,
            status: "failed",
          },
          window.location.origin,
        );
        setTimeout(() => {
          window.close();
        }, 1500);
        return;
      }

      // Same-tab flow: return to order details for the failed payment order
      setTimeout(() => {
        navigate("/account/orders", {
          replace: true,
          state: {
            paymentFailed: true,
            orderId,
            message: error.message || t("Payment verification failed"),
          },
        });
      }, 1200);
    }
  };

  // Extract order ID from URL path if not in query params
  const extractOrderIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const paymentIndex = pathParts.indexOf("payment");

    if (paymentIndex === -1) return null;

    const candidate = pathParts[paymentIndex + 2];
    const invalidCandidates = new Set(["success", "error", "callback"]);

    if (!candidate || invalidCandidates.has(candidate.toLowerCase())) {
      return null;
    }

    return candidate;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <div className="w-20 h-20 bg-[#0d223f] rounded-full mx-auto mb-6 flex items-center justify-center">
              <Loader size={40} className="text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("Processing Payment")}
            </h2>
            <p className="text-gray-600">
              {t("Please wait while we verify your payment...")}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center animate-bounce-in">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("Payment Successful!")}
            </h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">
              {t("Redirecting you to order confirmation...")}
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <XCircle size={40} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("Payment Failed")}
            </h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">
              {t("Redirecting you to your order details...")}
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PaymentCallback;
